import React, { useState, useRef, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Bot, User, Send, Sparkles, MessageCircle, Zap, Award, Plus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

const AIChat = () => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hello! I'm your AI assistant for program creation and management. I can help you create loyalty programs, design reward structures, set up member tiers, and much more. What would you like to create today?",
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Suggested prompts for quick start
  const suggestedPrompts = [
    "Create me a 3-tier program where silver holders earn 1 point per $5, and gold holders earn 1.5 points per $5, with points expiring in 12 months",
    "Design a cashback program with 2% rewards for groceries and 1% for everything else",
    "Set up a referral program that gives 500 bonus points for each successful referral",
    "Create a birthday rewards program that gives members a special discount on their birthday month",
    "Design a VIP program with exclusive access to premium rewards and early sale notifications",
    "Build a seasonal promotion that doubles points during holiday shopping periods",
    "Create a milestone program that rewards members for reaching spending thresholds"
  ];

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast({
        title: "Empty message",
        description: "Please enter a message to send.",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    try {
      // Simulate AI processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate AI response based on user input
      const aiResponse = generateAIResponse(message);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    if (input.includes('tier') || input.includes('level')) {
      return `Great idea! I'll help you create a tiered loyalty program. Based on your request, here's what I recommend:

**Program Structure:**
• **Silver Tier**: 1 point per $5 spent (20% earning rate)
• **Gold Tier**: 1.5 points per $5 spent (30% earning rate)
• **Platinum Tier**: 2 points per $5 spent (40% earning rate)

**Key Features:**
• Points expire after 12 months to encourage engagement
• Tier advancement based on annual spending or points earned
• Exclusive perks for each tier (early access, bonus multipliers, etc.)

**Recommended Tier Thresholds:**
• Silver: $0-$999 annual spend
• Gold: $1,000-$4,999 annual spend  
• Platinum: $5,000+ annual spend

Would you like me to help you set up the redemption catalog or configure any specific rewards for each tier?`;
    }
    
    if (input.includes('cashback') || input.includes('cash back')) {
      return `Perfect! Let's design a cashback program for you:

**Cashback Structure:**
• **Groceries**: 2% cashback on all grocery purchases
• **Gas Stations**: 1.5% cashback on fuel purchases  
• **Everything Else**: 1% cashback on all other purchases

**Program Features:**
• Monthly cashback statements
• Minimum redemption threshold of $25
• Cashback never expires
• Bonus categories that rotate quarterly

**Additional Recommendations:**
• Consider adding bonus cashback rates for online shopping (2.5%)
• Implement a referral bonus system
• Add seasonal promotions with elevated rates

Would you like me to help you set up the merchant categories or configure the redemption process?`;
    }
    
    if (input.includes('referral')) {
      return `Excellent choice! Referral programs are highly effective. Here's my recommendation:

**Referral Program Structure:**
• **Referrer Reward**: 500 bonus points for each successful referral
• **Referee Reward**: 250 welcome bonus points after first purchase
• **Tier Bonuses**: Higher tiers get additional referral multipliers

**Key Features:**
• Unlimited referrals allowed
• 30-day window for referee to make qualifying purchase
• Social sharing integration for easy referrals
• Tracking dashboard for referral status

**Bonus Structure:**
• Silver: Standard 500 points
• Gold: 750 points per referral
• Platinum: 1,000 points per referral

Would you like me to help you set up the referral tracking system or design the sharing mechanics?`;
    }
    
    if (input.includes('birthday') || input.includes('special')) {
      return `What a thoughtful program idea! Here's my birthday rewards recommendation:

**Birthday Program Features:**
• **Birthday Month Bonus**: 2x points on all purchases during birthday month
• **Special Birthday Gift**: 1,000 bonus points on member's birthday
• **Exclusive Birthday Discount**: 15% off one purchase during birthday month
• **Extended Offer**: Birthday rewards valid for full month, not just one day

**Enhanced Features:**
• Birthday surprise delivery for VIP members
• Early access to birthday month sales
• Personalized birthday offers based on purchase history
• Family birthday tracking for household accounts

**Communication Strategy:**
• Birthday month welcome email
• Mid-month reminder if not used
• Week-before-expiry notification

Would you like me to help you set up the birthday tracking system or design personalized offer mechanics?`;
    }
    
    // Default response for general queries
    return `I understand you're looking to create something special! Based on your request, I can help you design a comprehensive program solution.

**What I can help you create:**
• Multi-tier loyalty programs with custom earning rates
• Cashback and rewards structures
• Referral and social sharing programs
• Seasonal and promotional campaigns
• VIP and exclusive access programs
• Points expiration and engagement strategies

**Next Steps:**
To give you the most relevant recommendations, could you tell me more about:
• Your target audience or member base
• Preferred reward types (points, cashback, perks)
• Budget considerations or program goals
• Any specific features you'd like to include

Feel free to ask me about any specific aspect of program creation, and I'll provide detailed guidance tailored to your needs!`;
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setMessage(prompt);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="container mx-auto py-6 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
          <MessageCircle className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">AI Program Creator</h1>
          <p className="text-gray-600">Chat with AI to create and design your loyalty programs</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 flex-1 h-full">
        {/* Suggested Prompts Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              Quick Start Ideas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {suggestedPrompts.map((prompt, index) => (
                  <Button 
                    key={index}
                    variant="outline" 
                    className="w-full text-left h-auto py-3 px-3 text-sm whitespace-normal"
                    onClick={() => handleSuggestedPrompt(prompt)}
                  >
                    <div className="flex items-start gap-2 w-full">
                      <Zap className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span className="text-left">{prompt}</span>
                    </div>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
        
        {/* Chat Interface */}
        <Card className="lg:col-span-3 flex flex-col h-[600px]">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-blue-500" />
              AI Assistant Chat
              <Badge variant="secondary" className="ml-auto">
                <Award className="h-3 w-3 mr-1" />
                Program Creator
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 min-h-0">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-6 min-h-0" ref={scrollAreaRef}>
              <div className="space-y-4 pr-4">
                {messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.type === 'ai' && (
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    )}
                    
                    <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-first' : ''}`}>
                      <div 
                        className={`rounded-lg px-4 py-3 ${
                          msg.type === 'user' 
                            ? 'bg-blue-500 text-white ml-auto' 
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {msg.content}
                        </div>
                      </div>
                      <div className={`text-xs text-gray-500 mt-1 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    
                    {msg.type === 'user' && (
                      <div className="h-8 w-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-lg px-4 py-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {/* Message Input */}
            <div className="border-t p-4 flex-shrink-0">
              <div className="flex gap-3">
                <Textarea 
                  placeholder="Describe the program you want to create..." 
                  className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={isLoading || !message.trim()}
                  className="px-6"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                <span>Press Enter to send, Shift+Enter for new line</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIChat;
